import { createRoot } from "react-dom/client";
import { markStale } from "@/lib/staleVersion";

const root = document.getElementById("root")!;

// ---------------------------------------------------------------------------
// Service Worker — caches hashed Vite assets so that stale chunk requests
// after a deployment are served from cache instead of 404-ing. If the chunk
// is missing from both network and cache the SW marks the app as stale so
// that the next user-initiated navigation does a hard reload.
// ---------------------------------------------------------------------------
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/sw.js")
    .catch((err) => console.warn("[SW] registration failed", err));

  navigator.serviceWorker.addEventListener("message", (e) => {
    if (e.data?.type !== "STALE_ASSET_RELOAD") return;
    markStale();
  });
}

// ---------------------------------------------------------------------------
// Vite preload error — fallback for edge cases the SW hasn't cached yet
// (e.g. very first page load before the SW is active).
// We intentionally do NOT call e.preventDefault() — preventing the error
// causes Vite to resolve the dynamic import with an undefined module,
// which makes the router think the route loaded successfully and navigate
// to a broken page. By letting the error propagate, the router treats
// preloads as failed (silent) and actual navigations hit the error boundary.
// The beforeLoad stale check in __root.tsx handles the hard-nav on click.
// ---------------------------------------------------------------------------
window.addEventListener("vite:preloadError", () => {
  markStale();
});

window.addEventListener("error", (e) => {
  if (!e.error) return;
  console.error("[Unhandled error]", e.error);
});
window.addEventListener("unhandledrejection", (e) => {
  if (!e.reason) return;
  e.preventDefault();
  console.error("[Unhandled rejection]", e.reason);
});

const bootstrap = async () => {
  const { applyBrandTheme } = await import("@/lib/applyBrandTheme");
  applyBrandTheme();
  const { initializeI18n } = await import("@/lib/i18n");
  const i18n = await initializeI18n();
  const { default: App } = await import("./App");
  createRoot(root).render(<App i18n={i18n} />);
};

bootstrap().catch((err) => {
  console.error("[App bootstrap failed]", err);
  root.innerHTML =
    '<div style="padding:2rem;font-family:sans-serif;text-align:center"><h1>Something went wrong</h1><p>Please reload the page.</p></div>';
});
